import { AbstractShape } from "../abstract/Shape";
import { CanvasItem } from "../clases/CanvasItem";
import { Connection } from "../clases/Connection";
import { Values } from "../clases/Values";
import { IArrowValues } from "../interfaces/ArrowValues";
import { extractTranslateValues } from "./utils";

export function getInnerOffset(currentTransform: string): Values {
    let translation = (currentTransform) ? currentTransform : 'translate3d(0px, 0px, 0px)';
    let values = extractTranslateValues(translation)?.groups;
    let trX = parseInt(values!['x']);
    let trY = parseInt(values!['y']);
    return { x: trX, y: trY } as Values;
}

export function calculateResize(dX: number, dY: number, kit: HTMLElement) {
    const targetRect = kit.getBoundingClientRect();
    const width = targetRect.width + dX;
    const height = targetRect.height + dY;
    return { width, height };
}

export function positionBoxes(canvasWidth: number, canvasHeight: number, boxList: AbstractShape[]){
    let currentX = 15;
    let currentY = 15;
    let maxHeightInRow = 0;

    for (const box of boxList) {
        if (currentX + box.width <= canvasWidth) {
            box.x = currentX;
            box.y = currentY

            currentX += box.width + 30;
            maxHeightInRow = Math.max(maxHeightInRow, box.height);
        } else {
            currentX = 15;
            currentY += maxHeightInRow + 30;
            maxHeightInRow = box.height;

            box.x = currentX;
            box.y = currentY;
            currentX += box.width;
        }

        if (box.children.length > 0) {
            positionBoxes(box.width, box.height, box.children);
        }
    }
}

export function resizeBoxes(container: AbstractShape, minBoxSize: number, maxCols: number = 4): void {
    if (container.children.length > 0) {
        // Calculate total area for all children
        let totalArea = 0;
        container.children.forEach(child => {
            resizeBoxes(child, minBoxSize, maxCols);
            totalArea += child.width * child.height;
        });

        // Determine the number of columns and rows
        let cols = Math.min(maxCols, container.children.length);
        let rows = Math.ceil(container.children.length / cols);

        // Calculate new container size based on children
        let width = cols * minBoxSize;
        let height = rows * minBoxSize;

        // Calculate the actual height needed based on children's heights
        let maxHeightInEachCol = new Array(cols).fill(0);
        container.children.forEach((child, index) => {
            let colIndex = index % cols;
            maxHeightInEachCol[colIndex] += child.height;
        });
        let actualHeight = Math.max(...maxHeightInEachCol);

        // Set container size, excluding the extra padding
        // container.width = Math.max(width, minBoxSize) + 100;
        // container.height = Math.max(actualHeight, minBoxSize) + 100;
        let additional = (width + height) / 2;
        container.setSize(Math.max(width, minBoxSize) + additional, Math.max(actualHeight, minBoxSize) + additional);
    } else {
        // container.width = minBoxSize;
        // container.height = minBoxSize;
        container.setSize(minBoxSize, minBoxSize);
    }
}

export function positionInCircle(container: AbstractShape[], centerPos: Values, padding: number, radiusIncrement: number = 300) {
    container.sort((a, b) => (b.width * b.height) - (a.width * a.height));
    let radius = radiusIncrement;
    let angleIncrement = (2 * Math.PI) / container.length;
    container.forEach((child, index) => {
        let angle = index * angleIncrement; 
        child.x = centerPos.x + (radius + padding) * Math.cos(angle);
        child.y = centerPos.y + (radius + padding) * Math.sin(angle);
        // Recurse to position the child's children
        let newCenterPos = new Values((child.width / 2) - 80, (child.height / 2)-85);
        // positionInCircle(child.children, newCenterPos, (padding/16), (radiusIncrement/3.5) + (padding/16));
        masonryLayout(child.children, child.width-35, 4);
        adjustResize(child);
    });
    
}

export function adjustResize(child: AbstractShape){
    if (child.children && child.children.length > 0) {
        let largest = child.children.sort((a, b) => (b.y + b.height) - (a.y + a.height))[0];
        let additonalHeight = largest.height / 5;
        let height = largest.y + largest.height + additonalHeight;

        let largestW = child.children.sort((a, b) => (b.width + b.x) - (a.width + a.x))[0];
        let additonalWidth = largestW.width / 17;
        let width = largestW.x + largestW.width + additonalWidth;

        child.setSize(width, height);
    }
}

// export function masonryLayout(container: AbstractShape[], containerWidth: number, columnCount: number, rowMargin: number, leftMargin: number = 20, topMargin: number = 38) {
export function masonryLayout(container: AbstractShape[], containerWidth: number, columnCount: number) {

    // let leftMargin: number = 20;
    // let topMargin: number = 38;
    let leftMargin: number = Math.round(containerWidth / 18);
    let topMargin: number = Math.round(containerWidth / 17);
    let rowMargin: number = Math.round(containerWidth / 20);

    let columns = Array(columnCount).fill(topMargin); // Initialize columns height array
    let columnWidth = (containerWidth-leftMargin) / columnCount;

    container.forEach(child => {
        // Recursively position the child containers
        if (child.children && child.children.length > 0) {
            let newContainerWidth = child.width;
            let newColumnCount = Math.floor(newContainerWidth / columnWidth);
            newColumnCount = newColumnCount > 0 ? newColumnCount : 1; // Ensure at least one column
            // masonryLayout(child.children, newContainerWidth - 35, newColumnCount, rowMargin, leftMargin, topMargin);
            masonryLayout(child.children, newContainerWidth, newColumnCount);
            adjustResize(child);
        }
        // Calculate the number of columns this container will span
        let span = Math.ceil((child.width + leftMargin)/ columnWidth);
        span = Math.min(span, columnCount); // Ensure span does not exceed the total number of columns

        // Find the best column to place this container
        let minHeight = Infinity;
        let bestCol = 0;
        for (let i = 0; i <= columnCount - span; i++) {
            // Find the highest column height within the span
            let colHeight = Math.max(...columns.slice(i, i + span));
            if (colHeight < minHeight) {
                minHeight = colHeight;
                bestCol = i;
            }
        }

        // Update the container's position
        child.x = bestCol * columnWidth + leftMargin;
        child.y = minHeight + rowMargin; // Add margin between rows

        // let x = bestCol * columnWidth + leftMargin;
        // let y = minHeight + rowMargin; // Add margin between rows
        // child.setPosition(x, y);

        // Update the height of each column spanned by this container
        for (let i = bestCol; i < bestCol + span; i++) {
            columns[i] = minHeight + child.height + rowMargin; // Include margin in the column height
        }

        //fixing height.
        adjustResize(child);
    });
}

export function gridLayoutCenterOut(container: CanvasItem[], containerWidth: number, rowMargin: number, colMargin: number, minBoxSize: number) {
    const centerX = Math.floor(containerWidth / 2);
    const centerY = Math.floor(containerWidth / 2); // Assuming container is a square for simplicity
    let currentX = centerX;
    let currentY = centerY;
    let direction = 0; // 0: right, 1: down, 2: left, 3: up
    let stepsInCurrentDirection = 1;
    let stepsTaken = 0;
    let changeDirectionAfter = 1;
    let stepIncrement = 1;

    container.forEach((child, index) => {
        if (child.children.length>0) {
            masonryLayout(child.children, child.width - 35, 4);
            adjustResize(child);
        }

        // Position the child
        child.x = currentX - Math.floor(child.width / 2);
        child.y = currentY - Math.floor(child.height / 2);

        // Update positions for the next child
        switch (direction) {
            case 0: // Right
                currentX += child.width + colMargin;
                break;
            case 1: // Down
                currentY += child.height + rowMargin;
                break;
            case 2: // Left
                currentX -= child.width + colMargin;
                break;
            case 3: // Up
                currentY -= child.height + rowMargin;
                break;
        }

        stepsTaken++;
        if (stepsTaken === changeDirectionAfter) {
            direction = (direction + 1) % 4; // Change direction
            stepsTaken = 0;
            stepIncrement++;
            if (direction === 0 || direction === 2) {
                changeDirectionAfter = stepIncrement;
            } else {
                changeDirectionAfter = Math.floor(stepIncrement / 2);
            }
        }
    });
}

// Function to initialize nodes with random positions closer to the center
function initializeNodes(container: AbstractShape[], width: number, height: number) {
    const centerX = width / 2;
    const centerY = height / 2;
    const spread = Math.min(width, height) / 8; // Adjust spread as needed
  
    container.forEach(node => {
      node.x = centerX + (Math.random() - 0.5) * spread;
      node.y = centerY + (Math.random() - 0.5) * spread;
      node.vx = 0;
      node.vy = 0;
    });
  }
  
  // Function to calculate the attractive force
  function calculateAttractiveForce(k: number, dist: number) {
    return (dist * dist) / k;
  }
  
  // Function to calculate the repulsive force
  function calculateRepulsiveForce(k: number, dist: number) {
    return (k * k) / dist;
  }
  
  // Function to update the positions of the nodes based on forces
  function updatePositions(container: AbstractShape[], width: number, height: number, temperature: number) {
    const damping = 0.85; // Damping factor to reduce velocity over time
  
    container.forEach(node => {
      node.vx = Math.min(Math.max(node.vx, -temperature), temperature); // Limit velocity to the temperature value
      node.vy = Math.min(Math.max(node.vy, -temperature), temperature);
  
    //   node.x += node.vx;
    //   node.y += node.vy;

      node.move(node.vx, node.vy);
  
      // Ensure nodes stay within bounds
    //   node.x = Math.max(node.width / 2, Math.min(width - node.width / 2, node.x));
    //   node.y = Math.max(node.height / 2, Math.min(height - node.height / 2, node.y));
  
      // Apply damping to velocities
      node.vx *= damping;
      node.vy *= damping;
    });
  }
  
  // Function to implement the Fruchterman-Reingold algorithm
  export function fruchtermanReingold(containers: AbstractShape[], connections: Connection[], width: number, height: number, iterations: number, temperature: number, widthPos: number = 500, heightPos: number = 500) {
    const area = width * height;
    const k = Math.sqrt(area / containers.length);
    const centerX = widthPos / 2;
    const centerY = heightPos / 2;

    for (const item of containers) {
        if (item.children.length>0) {
            //here change
            masonryLayout(item.children, item.width, 4);
            adjustResize(item);
        }
    }
  
    initializeNodes(containers, width, height);
  
    for (let i = 0; i < iterations; i++) {
      // Calculate repulsive forces
      containers.forEach(node => {
        node.vx = 0;
        node.vy = 0;
        containers.forEach(otherNode => {
          if (node !== otherNode) {
            const dx = node.x - otherNode.x;
            const dy = node.y - otherNode.y;
            const dist = Math.sqrt(dx * dx + dy * dy) + 0.01; // Add small value to avoid division by zero
            const repulsiveForce = calculateRepulsiveForce(k, dist);
            node.vx += (dx / dist) * repulsiveForce;
            node.vy += (dy / dist) * repulsiveForce;
          }
        });
      });
  
      // Calculate attractive forces based on connections
      connections.forEach(connection => {
        const source = containers.find(node => node.id === connection.from);
        const target = containers.find(node => node.id === connection.to);
        if (source && target) {
          const dx = source.x - target.x;
          const dy = source.y - target.y;
          const dist = Math.sqrt(dx * dx + dy * dy) + 0.01;
          const attractiveForce = calculateAttractiveForce(k, dist);
          source.vx -= (dx / dist) * attractiveForce;
          source.vy -= (dy / dist) * attractiveForce;
          target.vx += (dx / dist) * attractiveForce;
          target.vy += (dy / dist) * attractiveForce;
        }
      });
  
      // Add stronger center-pulling force
      containers.forEach(node => {
        const dx = node.x - centerX;
        const dy = node.y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy) + 0.01;
        const centerPullForce = calculateAttractiveForce(k, dist) * 15; // Stronger pull towards the center
        node.vx -= (dx / dist) * centerPullForce;
        node.vy -= (dy / dist) * centerPullForce;
      });
  
      // Update positions
      updatePositions(containers, width, height, temperature);
  
      // Cool temperature (annealing)
      temperature *= 0.95;
    }
  }
  
  
// export function calculateArrowPosition(from: AbstractShape, to: AbstractShape, mainItems: AbstractShape[]): IArrowValues {
//     let fromOffset = from!.calculateAllOffsets(mainItems);
//     let toOffset = to!.calculateAllOffsets(mainItems);
//     let centerFrom = from!.findItemCenter();
//     let centerto = to!.findItemCenter();
//     let fromPos = from.getPosition();
//     let toPos = to.getPosition();
//     let x1 = fromPos.x + fromOffset.x + centerFrom.x;
//     let y1 = fromPos.y + fromOffset.y + centerFrom.y;
//     let x2 = toPos.x + toOffset.x + centerto.x;
//     let y2 = toPos.y + toOffset.y + centerto.y;
//     return { x1, y1, x2, y2 } as IArrowValues;
// }

export function calculateArrowPosition(from: AbstractShape, to: AbstractShape, mainItems: AbstractShape[]): IArrowValues {
    let fromOffset = from!.calculateAllOffsets(mainItems);
    let toOffset = to!.calculateAllOffsets(mainItems);
    let fromPos = from.getPosition();
    let toPos = to.getPosition();
    let x1 = fromPos.x + fromOffset.x;
    let y1 = fromPos.y + fromOffset.y;
    let x2 = toPos.x + toOffset.x;
    let y2 = toPos.y + toOffset.y;
    return { x1, y1, x2, y2 } as IArrowValues;
}

export function calculateConnectionPosition(from: AbstractShape, mouse: Values, mainItems: AbstractShape[]): IArrowValues {
    let fromOffset = from.calculateAllOffsets(mainItems);
    let centerFrom = from.findItemCenter();
    let fromPos = from.getPosition();
    // let x1 = fromPos.x + fromOffset.x + centerFrom.x;
    // let y1 = fromPos.y + fromOffset.y + centerFrom.y;
    let x1 = fromPos.x + fromOffset.x;
    let y1 = fromPos.y + fromOffset.y;
    let x2 = mouse.x;
    let y2 = mouse.y;
    return { x1, y1, x2, y2 } as IArrowValues;
}